import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, ArrowRight, Activity } from "lucide-react";
import api from "../lib/api";
import SentimentMeter from "./SentimentMeter";

export default function MarketSentimentWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    api
      .get("/sentiment/market")
      .then((r) => !cancel && setData(r.data))
      .catch(() => !cancel && setData(null))
      .finally(() => !cancel && setLoading(false));
    return () => {
      cancel = true;
    };
  }, []);

  if (loading) {
    return (
      <div className="rounded-2xl bg-white border border-[#EAE6E1] p-6 flex items-center gap-2 text-sm text-[#6B7068]">
        <Loader2 className="w-4 h-4 animate-spin" /> Reading market sentiment...
      </div>
    );
  }

  if (!data || (!data.spy && !data.btc)) {
    return null;
  }

  return (
    <div
      className="rounded-2xl bg-white border border-[#EAE6E1] p-6"
      data-testid="market-sentiment-widget"
    >
      <div className="flex items-end justify-between mb-5">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#6B7068] mb-1 flex items-center gap-1.5">
            <Activity className="w-3 h-3 text-[#D18E63]" strokeWidth={2.5} />
            Market sentiment
          </div>
          <h2 className="font-heading text-2xl tracking-tight text-[#2A2F2B]">
            How the room feels today
          </h2>
        </div>
        {data.fear_greed && (
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#6B7068]">Crypto Fear & Greed</div>
            <div className="font-mono-ticker text-lg font-semibold text-[#2A2F2B]">
              {data.fear_greed.raw_value}
              <span className="text-xs font-normal text-[#6B7068] ml-1">{data.fear_greed.label}</span>
            </div>
          </div>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {data.spy && (
          <Link
            to="/asset/stock/SPY"
            data-testid="market-sentiment-spy"
            className="flex items-center gap-5 rounded-xl border border-[#EAE6E1] p-4 hover:border-[#547A8F] hover:bg-[#F9F8F6] transition-soft"
          >
            <SentimentMeter score={data.spy.score} label={data.spy.label} size="sm" />
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#6B7068]">US Stocks</div>
              <div className="font-mono-ticker text-xl font-bold text-[#2A2F2B]">SPY</div>
              <div className="text-xs text-[#6B7068] mt-1">
                VIX {data.vix?.raw_value ?? "—"}
                <span className="ml-1 text-[10px]">({data.vix?.label || "—"})</span>
              </div>
              <div className="mt-2 inline-flex items-center gap-1 text-xs text-[#547A8F] font-semibold">
                Open chart <ArrowRight className="w-3 h-3" />
              </div>
            </div>
          </Link>
        )}
        {data.btc && (
          <Link
            to="/asset/crypto/BTC"
            data-testid="market-sentiment-btc"
            className="flex items-center gap-5 rounded-xl border border-[#EAE6E1] p-4 hover:border-[#D18E63] hover:bg-[#F9F8F6] transition-soft"
          >
            <SentimentMeter score={data.btc.score} label={data.btc.label} size="sm" />
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#6B7068]">Crypto</div>
              <div className="font-mono-ticker text-xl font-bold text-[#2A2F2B]">BTC</div>
              <div className="text-xs text-[#6B7068] mt-1">
                Fear &amp; Greed {data.fear_greed?.raw_value ?? "—"}
                <span className="ml-1 text-[10px]">({data.fear_greed?.label || "—"})</span>
              </div>
              <div className="mt-2 inline-flex items-center gap-1 text-xs text-[#D18E63] font-semibold">
                Open chart <ArrowRight className="w-3 h-3" />
              </div>
            </div>
          </Link>
        )}
      </div>
    </div>
  );
}
